import type { ShortlistGroup } from '@elara/client-core/models/ShortlistGroupModel';
import type { ShortlistItem } from '@elara/client-core/models/ShortlistItemModel';
import type { RoomShortlistMetadata } from '@elara/domain/models/schemas/shortlist-schemas';

import { isRateAvailableForMetadata } from '@elara/client-core/lib/occupancy-utils';

import { useCanRefreshPricing } from '#lib/can-refresh-pricing.js';
import { useRefreshPricing } from '#lib/useRefreshPricing.js';
import { ItineraryButton } from '#ui/itinerary-button.js';

import { Button } from '../button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../dropdown-menu.js';
import { IconSizes, MaterialIcon } from '../MaterialIcon.js';

interface ShortlistItemActionsMenuProps {
  item: ShortlistItem;
  groups: ShortlistGroup[];
  groupsWithItem: Set<string>;
  onNavigateToMessage: () => void;
  onMoveToGroup: (groupId: string) => Promise<void>;
  onCopyToGroup: (groupId: string) => Promise<void>;
  onRemove: () => Promise<void>;
}

export function ShortlistItemActionsMenu({
  item,
  groups,
  groupsWithItem,
  onNavigateToMessage,
  onMoveToGroup,
  onCopyToGroup,
  onRemove,
}: ShortlistItemActionsMenuProps) {
  const model = item.type === 'flight' ? item.flightOption : item.type === 'room' ? item.roomOption : undefined;
  const canRefreshEnabled = useCanRefreshPricing(item.type, model);
  const {
    canRefresh: canRefreshPricing,
    isRefreshing,
    refresh: handleRefreshPricing,
  } = useRefreshPricing({
    type: item.type as 'flight' | 'room',
    referenceId: item.referenceId,
    enabled: canRefreshEnabled,
    label: item.displayTitle,
  });

  // Rooms are left out: the shortlist saves a room, not a rate, while the itinerary books a rate. The
  // room guide (a tap on the card) is where the room's rates are, and where one goes on the itinerary.
  const canAddToItinerary = ['flight', 'place'].includes(item.type);
  const roomMetadata = item.type === 'room' ? (item.metadata as Partial<RoomShortlistMetadata> | undefined) : undefined;

  // The same rule as the card: a pinned rate's own availability, else whether the room has any rate left.
  const isRoomAvailable = item.type === 'room' ? isRateAvailableForMetadata(item.roomOption, roomMetadata) : false;
  const isFlightAvailable = item.type === 'flight' ? item.flightOption?.isAvailable : undefined;
  const isUnavailable =
    (item.type === 'flight' && isFlightAvailable === false) || (item.type === 'room' && isRoomAvailable === false);

  if (isUnavailable) {
    return (
      <div className="flex items-center absolute top-0 right-0">
        {canRefreshPricing && (
          <Button
            variant="icon"
            size="icon-sm"
            onClick={handleRefreshPricing}
            disabled={isRefreshing}
            tooltip={isRefreshing ? 'Refreshing...' : 'Recheck availability'}
          >
            <MaterialIcon icon="refresh" className={`text-sm ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        )}
        <Button variant="icon" size="icon-sm" onClick={onRemove} tooltip="Remove">
          <MaterialIcon icon="delete" className="text-sm text-destructive" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center absolute top-0 right-0">
      {canAddToItinerary && (
        <ItineraryButton
          itemType={item.type as 'flight' | 'place'}
          referenceId={item.referenceId ?? undefined}
          title={item.displayTitle}
          messageRef={item.messageRef ?? undefined}
          metadata={item.metadata ?? undefined}
          variant="icon"
          size="icon-sm"
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="icon" size="icon-sm">
            <MaterialIcon icon="more_horiz" className="text-sm" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canRefreshPricing && (
            <>
              <DropdownMenuItem onClick={handleRefreshPricing} disabled={isRefreshing}>
                <MaterialIcon
                  icon="refresh"
                  className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
                  size={IconSizes.sm}
                />
                {isRefreshing ? 'Refreshing...' : 'Refresh pricing'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {item.hasMessageRef && (
            <DropdownMenuItem onClick={onNavigateToMessage}>
              <MaterialIcon icon="chat_paste_go" className="mr-2" size={IconSizes.sm} />
              View in chat
            </DropdownMenuItem>
          )}

          {/* Move to group submenu */}
          {groups.length > 0 && (
            <>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <MaterialIcon icon="move_item" className="mr-2" size={IconSizes.sm} />
                  Move to&hellip;
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {groups.map((group) => {
                    const isAlreadyInGroup = groupsWithItem.has(group.id);
                    return (
                      <DropdownMenuItem
                        key={group.id}
                        onClick={async () => !isAlreadyInGroup && onMoveToGroup(group.id)}
                        disabled={isAlreadyInGroup}
                      >
                        {group.displayName}
                        {isAlreadyInGroup && <MaterialIcon icon="check" className="ml-auto text-sm" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <MaterialIcon icon="content_copy" className="mr-2" size={IconSizes.sm} />
                  Copy to&hellip;
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {groups.map((group) => {
                    const isAlreadyInGroup = groupsWithItem.has(group.id);
                    return (
                      <DropdownMenuItem
                        key={group.id}
                        onClick={async () => !isAlreadyInGroup && onCopyToGroup(group.id)}
                        disabled={isAlreadyInGroup}
                      >
                        {group.displayName}
                        {isAlreadyInGroup && <MaterialIcon icon="check" className="ml-auto text-sm" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem onClick={onRemove} className="text-destructive hover:text-destructive-foreground">
            <MaterialIcon icon="delete" className="mr-2 text-sm" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
